/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { Spinner } from 'react-bootstrap';
import {
  Mail,
  Checkbox,
  Signal,
  Linkedin,
  InfoCircle,
  Braces2,
  Whatsapp,
  ArrowsDiagonal,
  Calendar,
  ChevronDown,
} from '@saleshandy/icons';
import insertTextAtCursor from 'insert-text-at-cursor';
import classnames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { ImageIcon } from '@saleshandy/design-system';
import type { IProps } from './create-step-modal-container';
import Modal from '../../../../../shared/design-system/components/atoms/modal';
import Editor from '../../../../../shared/editor/editor';
import {
  getInitialSubType,
  getStepContentSectionLabel,
  getSubTypeSectionLabel,
  isSubTypeSectionVisible,
  getLinkedInType,
  getDaysAndStepsObj,
  getSequenceTypeTooltipText,
  isFormValid,
  contentMaxLength,
  subjectInputMaxLength,
  isSubmitDisabled,
  getErrorString,
  getStepInitialTaskNote,
  getIsAnyFieldDirty,
  getConfirmationModalContent,
  getMinLength,
  getPlaceHolderText,
  getIsExpandOptionVisible,
  getIsTaskNoteContent,
  getTaskNoteModalTitle,
  getTaskNoteModalMaxLength,
  getCallInType,
  PurposeOfCall,
  getWhatsappInType,
  WhatsAppTaskType,
  WHATSAPP_MAX_LIMIT,
  getEditorHeight,
} from './utils/helper';
import StepTaskType from './components/step-task-type';
import TaskNote from './components/task-note';
import TaskPriority from './components/task-priority';
import {
  SequenceStepAction,
  SequenceStepExecutionDay,
  SequenceStepPriorityValue,
  SequenceStepType,
  StepType,
} from '../../../enums';
import TaskAssignee from './components/task-assignee';
import InputNumber from '../../../../../shared/design-system/components/input-number';
import { validateRelativeDays } from '../add-step-modal/validator';
import { stepBgColor } from '../../../helpers/step-types';
import { accessibleOnClick } from '../../../../../shared/utils/accessible-on-click';
import { isAgencyClientUser } from '../../../../../shared/utils/user-details';
import OverlayMergeTags from '../../../../../shared/components/overlay-merge-tags';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { prepareMergeTagOptions } from '../email-modal/helpers';
import { getIsRequestPending } from '../../../helpers/helper';
import { executeOnRequestStatus } from '../../../../../shared/utils';
import toaster, { Theme } from '../../../../../shared/toaster';
import { showGeneralErrorNotification } from '../../../../../shared/utils/errors';
import { getStepScheduledString } from '../add-step-modal/helper';
import {
  getDefaultRelativeExecutionDay,
  getLastAbsoluteDay,
} from '../../../helpers/sequence-step';
import { getVariantNumberLetter } from '../../../helpers/sequence-step-variant';
import { StepFormDirty, StepFormError } from './type';
import { validate } from './utils/validator';
import SendTestEmailModal from '../send-test-email-modal';
import { Images } from '../../../../../shared/app-constants';
import { isAddVariantBtnVisible } from '../../../components/sequence-single-content/components/sequence-single-step-list/helpers/helper';
import TaskNoteModal from './components/task-note/components/task-note-modal';
import { handleDemoFeedbackModal } from '../../../../home/utils/demo-feedback-helper';
import {
  CALL_LOG_STEP_TYPES,
  isCallTask,
  isWhatsappTask,
} from '../../../../tasks/components/tasks-content/utils/helper';
import { getFormattedAttachmentsData } from '../variant-modal/helper';
import {
  insertChipForMergeTag,
  stripChipTagsFromHtml,
  convertHtmlToMergeTagFormat,
  getTagsOptionsForInlineDropdown,
  insertChipForDateTag,
  dateChipTags,
} from '../../../../../shared/editor/helpers';
import { convertHtmlToText } from '../email-modal/components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';
import SingleEditorInput from '../email-modal/components/single-editor-input';
import { br2nl } from '../email-modal/components/sidebar/tabs/utils/get-merge-tag-mapping-with-values';
import Input from '../../../../../shared/design-system/components/input/input';
import Icon from '../../../../../shared/design-system/components/atoms/icon';
import Checkboxes from '../../../../../shared/design-system/components/atoms/checkbox';
import { combineDateWithCurrentTime } from '../../../../prospect/components/prospect-list/components/prospect-details/prospect-details-modal/tasks-list-tab/utils/helper';

const stepTypeClasses = {
  [StepType.Email]: { active: 'gray-txt-1', inactive: 'blue-txt-24' },
  [StepType.LinkedIn]: { active: 'gray-txt-1', inactive: 'blue-txt-24' },
  [StepType.Call]: { active: 'gray-txt-1', inactive: 'purple-txt-16' },
  [StepType.WhatsApp]: { active: 'gray-txt-1', inactive: 'green-txt-23' },
  [StepType.Task]: { active: 'gray-txt-1', inactive: 'orange-txt-21' },
};

const stepTypeIcons = {
  [StepType.Email]: Mail,
  [StepType.LinkedIn]: Linkedin,
  [StepType.Call]: Signal,
  [StepType.WhatsApp]: Whatsapp,
  [StepType.Task]: Checkbox,
};

const getChannels = (stepType, excludeStepTypes = []) =>
  Object.keys(stepTypeIcons)
    .filter((key) => !excludeStepTypes.includes(key))
    .map((key) => {
      const IconComponent = stepTypeIcons[key];
      return {
        key,
        icon: (
          <IconComponent
            width={20}
            height={20}
            className={
              stepType === key
                ? stepTypeClasses[key].active
                : stepTypeClasses[key].inactive
            }
          />
        ),
      };
    });

const CreateStepModal: React.FC<IProps> = ({
  show,
  initialStepType,
  stepType,
  onClose,
  onStepTypeChange,
  sequenceId,
  mode = SequenceStepAction.CreateStep,
  actionStep,
  fields,
  taskAssigneeList,
  stepNumber,
  steps,
  variantNumber,
  userId,
  defaultAbsoluteDay,
  actionVariant,
  updatedSteps,

  addSequenceStepRequestStatus,
  addSequenceStepVariantRequestStatus,
  updateSequenceStepVariantDataRequestStatus,
  addSequenceStepRequestError,

  sendAddSequenceStepVariantRequest,
  sendAddSequenceStepRequest,
  sendGetFieldsRequest,
  sendGetSequenceRequest,
  sendGetSubsequenceRequest,
  sendGetTaskAssigneeListRequest,
  sendUpdateSequenceStepVariantDataRequest,
  resetAddStepRequest,
  subsequenceId,
  subsequenceStepNumber,
  subsequenceSteps,
  subsequenceSettings,

  // Add attachment and image upload related props
  uploadAttachmentRequestStatus,
  uploadAttachmentRequestError,
  uploadedAttachment,
  resetUploadAttachmentRequest,
  deleteAttachmentRequestStatus,
  deleteAttachmentRequestError,
  deletedAttachmentId,
  resetDeleteAttachmentRequest,
  sendUploadAttachmentRequest,
  sendDeleteAttachmentRequest,
  sendImageUploadRequest,
  excludeStepTypes = [],
  isTaskModal = false,
  sendCreateTaskRequest,
  prospectId,
  createTaskRequestStatus,
  className = '',
  getTaskAssigneeListRequestStatus,
  showCloseIcon = true,
  CallLogId,
  taskAssigneeFromDialer = false,
}) => {
  const contentRef = useRef<any>();
  const inMailRef = useRef<HTMLInputElement>(null);
  const inMailEditorRef = useRef<any>(null);
  const editorRef = useRef<any>(null);
  const mergeTagOptions = prepareMergeTagOptions(fields);
  const label = 'Start this step on Day ';
  const isSubsequence = Boolean(subsequenceId);

  //* State */
  const [absoluteDays, setAbsoluteDays] = useState<number>(
    isSubsequence && subsequenceStepNumber === 1
      ? subsequenceSettings?.firstStepRelativeDays
      : defaultAbsoluteDay ||
          getDefaultRelativeExecutionDay(
            isSubsequence ? subsequenceStepNumber : stepNumber,
            getLastAbsoluteDay(
              isSubsequence ? subsequenceSteps : steps,
              updatedSteps,
            ),
          ),
  );
  const [selectedStepType, setSelectedStepType] = useState<SequenceStepType>(
    initialStepType,
  );
  const [selectedStepSubType, setSelectedStepSubType] = useState<string>(
    getInitialSubType(initialStepType),
  );
  const [stepContentHTML, setStepContentHTML] = useState('');
  const [taskNote, setTaskNote] = useState<string>('');
  // WhatsApp-specific content state for Message type
  const [whatsappMessageContent, setWhatsappMessageContent] = useState<string>(
    '',
  );
  // WhatsApp-specific content state for Voice types (Voice Message and Voice Call)
  const [whatsappVoiceContent, setWhatsappVoiceContent] = useState<string>('');
  const [
    selectedPriority,
    setSelectedPriority,
  ] = useState<SequenceStepPriorityValue>(SequenceStepPriorityValue.Normal);
  const [selectedAssignee, setSelectedAssignee] = useState(userId);
  const [inMailValue, setInMailValue] = useState<string>('');
  const [buttonName, setButtonName] = useState<string>('');
  const [buttonLink, setButtonLink] = useState<string>('');
  const [errors, setErrors] = useState<StepFormError>({
    buttonName: false,
    buttonLink: false,
    inMail: false,
    content: false,
  });
  const [isDirty, setDirty] = useState<StepFormDirty>({
    buttonName: false,
    buttonLink: false,
    inMail: false,
    content: false,
    inMailSubject: false,
    customTitle: false,
  });
  const [
    isConfirmationModalVisible,
    setIsConfirmationModalVisible,
  ] = useState<boolean>(false);
  const [isAddTaskModalVisible, setAddTaskModalVisible] = useState<boolean>(
    false,
  );
  const [taskAssignee, setTaskAssignee] = useState(taskAssigneeList?.[0]?.id);

  // Add attachment state for Editor component
  const [attachments, setAttachments] = useState([]);
  const [attachmentsMeta, setAttachmentsMeta] = useState([]);
  const [uniqueId] = useState(Date.now());
  const [originalAttachmentIds, setOriginalAttachmentIds] = useState<number[]>(
    [],
  );

  // State to track WhatsApp editor character limit exceeded
  const [
    isWhatsappCharacterLimitExceeded,
    setIsWhatsappCharacterLimitExceeded,
  ] = useState(false);

  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [isTaskCompleted, setIsTaskCompleted] = useState(false);

  //* State End */

  //* Functions */
  const showModal = () => setAddTaskModalVisible(true);
  const hideModal = () => setAddTaskModalVisible(false);

  const handleDueDateChange = (date: Date | null) => {
    setDueDate(date);
    if (date) {
      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );
      const selectedDateStart = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      const diffTime = selectedDateStart.getTime() - todayStart.getTime();
      const diffDays = Math.max(
        1,
        Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1,
      );
      setAbsoluteDays(diffDays);
    } else {
      setAbsoluteDays(1);
    }
  };

  // Add attachment handlers for Editor component
  const handleAttachmentStatusChange = (attachment) => {
    setAttachments((prev) =>
      prev.map((att) =>
        att.attachmentIdentifier === attachment.attachmentIdentifier
          ? { ...att, ...attachment }
          : att,
      ),
    );
  };

  const handleAttachmentsChange = (
    newAttachments,
    isAttachmentDeleted = false,
  ) => {
    setAttachments(newAttachments);
    if (isAttachmentDeleted) {
      setAttachmentsMeta((prev) =>
        prev.filter((meta) =>
          newAttachments.some(
            (att) => att.attachmentIdentifier === meta.attachmentIdentifier,
          ),
        ),
      );
    }
  };

  const handleAttachmentsMetaChange = (meta) => {
    setAttachmentsMeta(meta);
  };

  const handleImageUploader = ({ formData, onUploadProgress }) =>
    sendImageUploadRequest({ formData, onUploadProgress });

  // Helper function to convert merge tags to chips in content
  const convertContentWithMergeTags = (content: string) => {
    if (!content || !mergeTagOptions?.length) return content;

    const tagsOptions = getTagsOptionsForInlineDropdown(mergeTagOptions, true);
    return convertHtmlToMergeTagFormat(content, tagsOptions, false); // Disable spin tag conversion
  };

  const checkWhatsappCharacterLimit = (content: string) => {
    if (selectedStepType === SequenceStepType.WhatsAppMessage) {
      const plainTextLength =
        content?.replace(/<[^>]*>/g, '')?.trim()?.length || 0;
      setIsWhatsappCharacterLimitExceeded(plainTextLength > WHATSAPP_MAX_LIMIT);
    } else {
      // Reset character limit state for non-Message WhatsApp types
      setIsWhatsappCharacterLimitExceeded(false);
    }
  };

  const handleAddMergeTagInContent = (option) => {
    const { tag } = option;
    const uuid = uuidv4();
    const chipHtml = insertChipForMergeTag(
      tag,
      uuid,
      false,
      true,
      option?.fieldType,
    );

    editorRef.current?.insertContent(chipHtml);
  };

  const handleAddVariableTagInContent = (tag) => {
    const uuid = uuidv4();
    let chipHtml: string;
    if (dateChipTags(tag)) {
      chipHtml = insertChipForDateTag(tag, uuid, true);
    } else {
      chipHtml = insertChipForMergeTag(tag, uuid, true, true);
    }
    // const chipHtml = insertChipForMergeTag(tag, uuid, true, true);

    editorRef.current?.insertContent(chipHtml);
  };

  const dayValidate = (value) => {
    const minRelativeDays = SequenceStepExecutionDay.MinDay;
    return validateRelativeDays(value, minRelativeDays);
  };

  const onKeyDownHandler = (evt) => {
    if (['e', 'E', '+', '-', '.'].includes(evt.key)) {
      evt.preventDefault();
    }
  };

  const onInputChangeHelper = (value) => {
    const error = dayValidate(value);

    if (!error || value === '') {
      setAbsoluteDays(value);
    }
  };

  const isPayloadRequired = () =>
    selectedStepType !== SequenceStepType.LinkedInPostInteraction &&
    selectedStepType !== SequenceStepType.LinkedInViewProfile;

  const onContentChange = (e) => {
    // Handle the conflict between LinkedInTaskType.Message and WhatsAppTaskType.Message
    // Both have the same enum value "Message", so we need to check stepType first
    let maxLength;
    if (
      stepType === StepType.WhatsApp &&
      selectedStepSubType === WhatsAppTaskType.Message
    ) {
      maxLength = WHATSAPP_MAX_LIMIT;
    } else {
      maxLength =
        contentMaxLength[selectedStepSubType] || contentMaxLength[stepType];
    }

    setDirty((preState) => ({ ...preState, content: true }));
    setErrors((preState) => ({
      ...preState,
      content:
        br2nl(convertHtmlToText(stripChipTagsFromHtml(e)))?.trim()?.length >
        maxLength,
    }));

    if (
      selectedStepType === SequenceStepType.LinkedInPostInteraction ||
      selectedStepType === SequenceStepType.LinkedInViewProfile ||
      isCallTask(selectedStepType) ||
      selectedStepType === SequenceStepType.Custom
    ) {
      setTaskNote(e);
      return;
    }

    setStepContentHTML(e);

    // Check WhatsApp character limit
    checkWhatsappCharacterLimit(e);

    // Save WhatsApp Message content separately
    if (selectedStepType === SequenceStepType.WhatsAppMessage) {
      setWhatsappMessageContent(e);
    }
    // Save WhatsApp Voice content separately
    else if (
      selectedStepType === SequenceStepType.WhatsAppVoiceMessage ||
      selectedStepType === SequenceStepType.WhatsAppVoiceCall
    ) {
      setWhatsappVoiceContent(e);
    }

    hideModal();
  };

  const onContentBlur = (isTaskNoteContent = false) => {
    // Handle the conflict between LinkedInTaskType.Message and WhatsAppTaskType.Message
    // Both have the same enum value "Message", so we need to check stepType first
    let maxLength;
    if (
      stepType === StepType.WhatsApp &&
      selectedStepSubType === WhatsAppTaskType.Message
    ) {
      maxLength = WHATSAPP_MAX_LIMIT;
    } else {
      maxLength =
        contentMaxLength[selectedStepSubType] || contentMaxLength[stepType];
    }

    // For LinkedIn connection request, validate against stripped content (without merge tag HTML)
    const contentToValidate =
      selectedStepType === SequenceStepType.LinkedInConnectionRequest
        ? stripChipTagsFromHtml(isTaskNoteContent ? taskNote : stepContentHTML)
        : isTaskNoteContent
        ? taskNote
        : stepContentHTML;

    setErrors((preState) => ({
      ...preState,
      content: validate(
        stepType,
        maxLength,
        {
          title: contentToValidate,
        },
        getMinLength(stepType, selectedStepType),
      ),
    }));
  };

  const handleStepType = (type) => {
    // Handle the conflict between LinkedInTaskType.Message and WhatsAppTaskType.Message
    // Both have the same enum value "Message", so we need to check stepType first
    let maxLength;
    if (
      type === StepType.WhatsApp &&
      selectedStepSubType === WhatsAppTaskType.Message
    ) {
      maxLength = WHATSAPP_MAX_LIMIT;
    } else {
      maxLength = contentMaxLength[type] || contentMaxLength[stepType];
    }
    const text = stepContentHTML || taskNote;

    setErrors((preState) => ({
      ...preState,
      inMail: false,
      content: br2nl(stripChipTagsFromHtml(text))?.trim()?.length > maxLength,
    }));

    // Handle WhatsApp-specific content switching
    if (stepType === StepType.WhatsApp) {
      const currentStepType = getWhatsappInType(type);

      // Reset character limit state when switching WhatsApp types
      setIsWhatsappCharacterLimitExceeded(false);

      // If switching to WhatsApp Message, restore the saved content
      if (currentStepType === SequenceStepType.WhatsAppMessage) {
        setStepContentHTML(whatsappMessageContent);
      }
      // If switching to Voice types, restore the saved voice content
      else if (
        currentStepType === SequenceStepType.WhatsAppVoiceMessage ||
        currentStepType === SequenceStepType.WhatsAppVoiceCall
      ) {
        setStepContentHTML(whatsappVoiceContent);
      }
      // If switching away from WhatsApp Message, save current content
      else if (selectedStepType === SequenceStepType.WhatsAppMessage) {
        setWhatsappMessageContent(stepContentHTML);
        setStepContentHTML('');
      }
      // If switching away from Voice types, save current content
      else if (
        selectedStepType === SequenceStepType.WhatsAppVoiceMessage ||
        selectedStepType === SequenceStepType.WhatsAppVoiceCall
      ) {
        setWhatsappVoiceContent(stepContentHTML);
        setStepContentHTML('');
      }
    }

    setSelectedStepSubType(type);

    if (stepType === StepType.Call) {
      setSelectedStepType(getCallInType(type));
    }
    if (stepType === StepType.LinkedIn) {
      setSelectedStepType(getLinkedInType(type));
    }
    if (stepType === StepType.WhatsApp) {
      setSelectedStepType(getWhatsappInType(type));
    }
    if (stepType === StepType.Task) {
      setSelectedStepType(SequenceStepType.Custom);
    }
  };

  const onInputBlur = () => {
    const maxLength = stepType === StepType.Task ? 100 : 200;

    // Convert HTML to plain text for validation
    const plainTextValue = convertHtmlToText(
      stripChipTagsFromHtml(inMailValue),
    );

    setErrors((preState) => ({
      ...preState,
      inMail: validate(
        stepType,
        maxLength,
        { title: plainTextValue },
        selectedStepType === SequenceStepType.LinkedInInMail ? 2 : 5,
      ),
    }));
  };
  const onInMailSubjectChange = (value: any, e) => {
    setDirty((preState) => ({ ...preState, inMail: true }));

    // Convert to plain text only for validation
    const stripValue = convertHtmlToText(stripChipTagsFromHtml(value));
    setErrors((preState) => ({
      ...preState,
      inMail: br2nl(stripValue)?.length > subjectInputMaxLength(stepType),
    }));

    // Store the HTML value, not the plain text
    setInMailValue(value);
  };

  //* API Call Functions */
  const getAPIPayload = () => {
    const payload: {
      subject?: string;
      connectionNote?: string;
      message?: string;
      title?: string;
      action?: {
        url?: string;
        name?: string;
      };
    } = {};

    if (selectedStepType === SequenceStepType.LinkedInInMail) {
      payload.subject = convertHtmlToText(
        stripChipTagsFromHtml(inMailValue),
      )?.trim();
    }

    if (isPayloadRequired()) {
      if (
        !isWhatsappTask(selectedStepType) &&
        selectedStepType !== SequenceStepType.LinkedInInMail &&
        selectedStepType !== SequenceStepType.LinkedInMessage &&
        selectedStepType !== SequenceStepType.Custom &&
        !isCallTask(selectedStepType)
      ) {
        payload.connectionNote = stripChipTagsFromHtml(stepContentHTML?.trim());
      }

      if (
        isWhatsappTask(selectedStepType) ||
        selectedStepType === SequenceStepType.LinkedInInMail ||
        selectedStepType === SequenceStepType.LinkedInMessage
      ) {
        payload.message = stripChipTagsFromHtml(stepContentHTML?.trim());
      }
    }

    if (
      selectedStepType === SequenceStepType.Custom &&
      inMailValue?.length > 0
    ) {
      payload.title = convertHtmlToText(
        stripChipTagsFromHtml(inMailValue),
      )?.trim();
    }

    if (
      selectedStepType === SequenceStepType.Custom &&
      buttonLink?.length > 0 &&
      buttonName?.length > 0
    ) {
      payload.action = {
        url: buttonLink,
        name: buttonName,
      };
    }

    return payload;
  };

  const onUpdateVariant = () => {
    // For WhatsApp messages, always send all fields except attachments
    if (selectedStepType === SequenceStepType.WhatsAppMessage) {
      const updatePayload: any = {
        type: selectedStepType,
        taskNote: stripChipTagsFromHtml(taskNote),
        ...(absoluteDays !== defaultAbsoluteDay && { absoluteDays }),
        priority: selectedPriority,
        ...(!isAgencyClientUser() && { assigneeId: selectedAssignee }),
        payload: getAPIPayload(),
      };

      const currentAttachmentIds =
        attachments
          ?.map((att) => att?.attachmentId)
          ?.filter((id) => id != null) || [];
      const newlyAddedAttachmentIds = currentAttachmentIds.filter(
        (id) => !originalAttachmentIds.includes(id),
      );

      if (newlyAddedAttachmentIds.length > 0) {
        updatePayload.attachmentIds = newlyAddedAttachmentIds;
      }

      sendUpdateSequenceStepVariantDataRequest(
        isSubsequence ? subsequenceId : sequenceId,
        actionStep?.id,
        actionVariant?.id,
        updatePayload,
      );
    } else {
      sendUpdateSequenceStepVariantDataRequest(
        isSubsequence ? subsequenceId : sequenceId,
        actionStep?.id,
        actionVariant?.id,
        {
          type: selectedStepType,
          taskNote: stripChipTagsFromHtml(taskNote),
          ...(absoluteDays !== defaultAbsoluteDay && { absoluteDays }),
          priority: selectedPriority,
          ...(!isAgencyClientUser() && { assigneeId: selectedAssignee }),
          ...(attachments?.length > 0 && {
            attachmentIds: attachments?.map((att) => att?.attachmentId),
          }),
          payload: getAPIPayload(),
        },
      );
    }
  };

  const onCreateVariant = () => {
    sendAddSequenceStepVariantRequest(
      isSubsequence ? subsequenceId : sequenceId,
      actionStep?.id,
      {
        type: selectedStepType,
        taskNote: stripChipTagsFromHtml(taskNote),
        priority: selectedPriority,
        ...(!isAgencyClientUser() && { assigneeId: selectedAssignee }),
        ...(absoluteDays !== defaultAbsoluteDay && {
          absoluteDays: Number(absoluteDays),
        }),
        ...(attachments?.length > 0 && {
          attachmentIds: attachments?.map((att) => att?.attachmentId),
        }),
        payload: getAPIPayload(),
      },
    );
  };

  const onSubmit = () => {
    // Convert to plain text for validation
    const plainTextValue = convertHtmlToText(
      stripChipTagsFromHtml(inMailValue),
    );

    if (
      !isFormValid(errors) ||
      (stepType === StepType.Task && plainTextValue?.length === 0)
    ) {
      return;
    }

    // Handle task modal - create task directly instead of sequence step
    if (isTaskModal && sendCreateTaskRequest && prospectId) {
      const assigneeIdForTask = taskAssignee ?? selectedAssignee;
      const taskPayload = {
        prospectId,
        type: selectedStepType,
        priority: selectedPriority,
        ...(!isAgencyClientUser() && {
          assigneeId: taskAssigneeFromDialer
            ? assigneeIdForTask
            : selectedAssignee,
        }),
        taskNote: stripChipTagsFromHtml(taskNote),
        payload: getAPIPayload(),
        ...(isTaskCompleted ? { taskAction: 0 } : {}),
        ...(dueDate && {
          dueDate: combineDateWithCurrentTime(dueDate).toString(),
        }),
        ...(attachments?.length > 0 && {
          attachmentIds: attachments
            ?.filter((att) => att?.attachmentId != null)
            ?.map((att) => att?.attachmentId),
        }),
        ...(CALL_LOG_STEP_TYPES.has(selectedStepType) && CallLogId
          ? { callId: CallLogId }
          : {}),
      };
      sendCreateTaskRequest(taskPayload);
      return;
    }

    if (mode === SequenceStepAction.CreateVariant) {
      onCreateVariant();
      return;
    }

    if (mode === SequenceStepAction.EditVariant) {
      onUpdateVariant();
      return;
    }

    sendAddSequenceStepRequest(isSubsequence ? subsequenceId : sequenceId, {
      type: selectedStepType,
      absoluteDays,
      priority: selectedPriority,
      ...(!isAgencyClientUser() && { assigneeId: selectedAssignee }),
      variants: [
        {
          taskNote: stripChipTagsFromHtml(taskNote),
          payload: getAPIPayload(),
          ...(attachments?.length > 0 && {
            attachmentIds: attachments?.map((att) => att?.attachmentId),
          }),
        },
      ],
    });
  };

  const handleClose = () => {
    if (getIsAnyFieldDirty(isDirty)) {
      setIsConfirmationModalVisible(true);
      return;
    }

    onClose();
  };

  //* useEffect */
  useEffect(() => {
    sendGetFieldsRequest();
    sendGetTaskAssigneeListRequest(isAgencyClientUser() || undefined);

    if (mode === SequenceStepAction.CreateVariant) {
      setSelectedPriority(actionStep?.priority);
      setOriginalAttachmentIds([]);
    }
    if (mode === SequenceStepAction.EditVariant) {
      const initialContent =
        actionVariant?.payload?.connectionNote ||
        actionVariant?.payload?.message ||
        actionVariant?.payload?.content ||
        '';

      // Convert merge tags to chips for initial content
      const convertedContent = convertContentWithMergeTags(initialContent);
      setStepContentHTML(convertedContent);
      setInMailValue(actionVariant?.payload?.title);

      setSelectedPriority(actionStep?.priority);
      setInMailValue(
        actionVariant?.payload?.subject || actionVariant?.payload?.title,
      );
      setButtonName(actionVariant?.payload?.action?.name);
      setButtonLink(actionVariant?.payload?.action?.url);
      setSelectedAssignee(actionStep?.assigneeId);

      // Load existing attachments for WhatsApp messages
      if (
        selectedStepSubType === WhatsAppTaskType.Message &&
        actionVariant?.sequenceStepVariantAttachments
      ) {
        const formattedAttachments = getFormattedAttachmentsData(
          actionVariant.sequenceStepVariantAttachments,
        );
        setAttachments(formattedAttachments);
        const originalIds =
          actionVariant.sequenceStepVariantAttachments?.map(
            (att) => att?.attachmentId,
          ) || [];
        setOriginalAttachmentIds(originalIds);
      } else {
        setOriginalAttachmentIds([]);
      }

      // Initialize WhatsApp Message content if editing WhatsApp Message
      if (actionStep?.type === SequenceStepType.WhatsAppMessage) {
        const whatsappContent = actionVariant?.payload?.message || '';
        const convertedWhatsappContent = convertContentWithMergeTags(
          whatsappContent,
        );
        setWhatsappMessageContent(convertedWhatsappContent);
      }
      // Initialize WhatsApp Voice content if editing Voice types
      else if (
        actionStep?.type === SequenceStepType.WhatsAppVoiceMessage ||
        actionStep?.type === SequenceStepType.WhatsAppVoiceCall
      ) {
        const voiceContent =
          actionVariant?.payload?.message ||
          actionVariant?.payload?.content ||
          '';
        const convertedVoiceContent = convertContentWithMergeTags(voiceContent);
        setWhatsappVoiceContent(convertedVoiceContent);
      }

      if (stepType === StepType.Call) {
        const callType = actionStep?.type;
        if (callType === SequenceStepType.Introduction) {
          setSelectedStepSubType(PurposeOfCall.Introduction);
        } else if (callType === SequenceStepType.Demo) {
          setSelectedStepSubType(PurposeOfCall.Demo);
        } else if (callType === SequenceStepType.FollowUp) {
          setSelectedStepSubType(PurposeOfCall.FollowUp);
        } else if (callType === SequenceStepType.Reminder) {
          setSelectedStepSubType(PurposeOfCall.Reminder);
        } else {
          setSelectedStepSubType(PurposeOfCall.Other);
        }
      }
    }

    const initialTaskNote =
      actionVariant?.taskNote ||
      getStepInitialTaskNote(
        isSubsequence ? subsequenceSteps : steps,
        actionStep,
      );

    // Convert merge tags to chips for task note
    const convertedTaskNote = convertContentWithMergeTags(initialTaskNote);
    setTaskNote(convertedTaskNote);
  }, []);

  // Set default assignee when taskAssigneeList is loaded
  useEffect(() => {
    executeOnRequestStatus({
      status: getTaskAssigneeListRequestStatus,
      onSuccess: () => {
        if (taskAssigneeList && taskAssigneeList.length > 0 && isTaskModal) {
          // For task modal, ensure taskAssignee is set to first assignee
          setTaskAssignee(taskAssigneeList[0].id);
        }
      },
    });
  }, [taskAssigneeList, isTaskModal, getTaskAssigneeListRequestStatus]);

  useEffect(() => {
    if (mode === SequenceStepAction.CreateStep) {
      setSelectedStepSubType(getInitialSubType(initialStepType));
      handleStepType(getInitialSubType(initialStepType));
      setInMailValue('');
      setTaskNote('');
      setStepContentHTML('');
      setWhatsappMessageContent('');
      setWhatsappVoiceContent('');
      setIsWhatsappCharacterLimitExceeded(false);
      setAttachments([]);
      setOriginalAttachmentIds([]);
      setErrors({
        buttonName: false,
        buttonLink: false,
        inMail: false,
        content: false,
      });
    }
  }, [stepType]);

  useEffect(() => {
    if (
      selectedStepType === SequenceStepType.WhatsAppMessage &&
      stepContentHTML
    ) {
      checkWhatsappCharacterLimit(stepContentHTML);
    }
  }, [selectedStepType, stepContentHTML]);

  useEffect(() => {
    executeOnRequestStatus({
      status: addSequenceStepRequestStatus,
      onSuccess: () => {
        toaster.success('Sequence step created successfully.', {
          theme: Theme.New,
        });

        handleDemoFeedbackModal();

        // changing a state when prop changes!!
        onClose();
        if (isSubsequence) {
          sendGetSubsequenceRequest(subsequenceId);
        } else {
          sendGetSequenceRequest(sequenceId);
        }
        resetAddStepRequest();
      },
      onFailed: () => {
        showGeneralErrorNotification(addSequenceStepRequestError.message);
        resetAddStepRequest();
      },
    });
  }, [addSequenceStepRequestStatus]);

  //* class variable */
  const stepWrapperClasses = classnames([
    'd-flex align-items-center pl-4 step-info-wrapper',
    {
      'step-info-wrapper-secondary': !isAddVariantBtnVisible(selectedStepType),
    },
  ]);
  const getChannelClasses = (key) =>
    classnames([
      'channel pointer',
      {
        'channel-disable':
          mode !== SequenceStepAction.CreateStep && key !== stepType,
      },
    ]);

  //* Render Functions */
  const renderHeader = () => (
    <div
      className={`d-flex justify-content-between ${!isTaskModal ? 'mt-4' : ''}`}
    >
      {!isTaskModal && (
        <div className={stepWrapperClasses}>
          <span className="regular-2 font-medium gray-txt-12">
            Step{' '}
            {mode !== SequenceStepAction.CreateStep
              ? actionStep?.number
              : isSubsequence
              ? subsequenceStepNumber
              : stepNumber}
          </span>
          {isAddVariantBtnVisible(selectedStepType) && (
            <span className="semibold-1 gray-txt-12 step-variant">
              {getVariantNumberLetter(variantNumber)}
            </span>
          )}
        </div>
      )}
      <div className="channels d-flex justify-content-center">
        {getChannels(stepType, excludeStepTypes).map(({ icon, key }, idx) => (
          <React.Fragment key={idx}>
            <div
              className={getChannelClasses(key)}
              style={{
                backgroundColor: key === stepType && stepBgColor[key],
                color: key === stepType && '#fff',
                opacity:
                  mode !== SequenceStepAction.CreateStep &&
                  key !== stepType &&
                  0.5,
                outline: key === stepType && 'none',
              }}
              {...accessibleOnClick(
                () =>
                  mode === SequenceStepAction.CreateStep &&
                  onStepTypeChange(key),
              )}
            >
              {icon}
            </div>
            {key === StepType.Email && !isTaskModal && (
              <div
                className="channel pointer"
                style={{
                  opacity:
                    mode !== SequenceStepAction.CreateStep ? 0.5 : undefined,
                }}
                {...accessibleOnClick(
                  () =>
                    mode === SequenceStepAction.CreateStep &&
                    onStepTypeChange(StepType.LinkedInAutomation),
                )}
              >
                <svg
                  width={20}
                  height={20}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4F46E5"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderMergeTagSelection = (isContentArea = true) => (
    <OverlayMergeTags
      options={mergeTagOptions}
      onChange={(option) => {
        const { tag } = option;
        if (isContentArea && editorRef.current) {
          handleAddMergeTagInContent(option);
          return;
        }

        if (!isContentArea && inMailEditorRef.current) {
          const uuid = uuidv4();
          const chipHtml = insertChipForMergeTag(
            tag,
            uuid,
            false,
            true,
            option?.fieldType,
          );

          inMailEditorRef?.current?.insertContent(chipHtml);
          return;
        }

        // Fallback to plain text insertion for non-editor inputs
        insertTextAtCursor(
          isContentArea ? contentRef?.current : inMailRef.current,
          option?.tag,
        );
      }}
      placement={Placement.BottomEnd}
      searchPlaceholder="Search Tags"
      className="merge-tags-dropdown"
      showAiGeneratedIcon
    >
      <div
        onMouseDown={(evt) => {
          evt.preventDefault(); // Avoids loosing focus from the editable area
        }}
        className="gray-16 merge-tag bs-border-radius-50 pointer"
      >
        <Braces2 width={20} height={20} className="pointer" />
      </div>
    </OverlayMergeTags>
  );

  // Check if we should use Editor component for WhatsApp messages
  const shouldUseEditor = selectedStepType === SequenceStepType.WhatsAppMessage;

  const getEditorClassName = () => {
    const height = getEditorHeight(stepType, selectedStepType);
    return `sequence-step-editor sequence-step-editor-height-${height
      .replace('px', '')
      .replace('rem', 'rem')}`;
  };

  // Compute plain text value for validation
  const plainTextInMailValue = convertHtmlToText(
    stripChipTagsFromHtml(inMailValue),
  );

  const hasErrorChips = stepContentHTML.includes('error-chip-container');

  const DatePickerInput = React.forwardRef<
    HTMLInputElement,
    React.HTMLProps<HTMLInputElement> & {
      onClear?: () => void;
    }
  >(({ value, onClick, onChange, onClear }, ref) => (
    <div className="bs-input date-picker-container">
      <Calendar />
      <input
        placeholder="Select Date"
        onClick={onClick}
        className="input bs-input"
        style={{ textIndent: 0 }}
        onChange={onChange}
        value={value ? value.toString() : ''}
        ref={ref}
        readOnly // important to avoid manual typing issues
      />

      <div className="d-flex align-items-center">
        {onClear && value && (
          <Icon
            className="cal-icon mr-1 sh-close close-icon"
            identifier="close"
            onClick={(e) => {
              e.stopPropagation();
              onClear?.();
            }}
          />
        )}
      </div>
    </div>
  ));
  return (
    <>
      <Modal
        show={show}
        className={`create-step-modal ${className}`}
        titleContent={renderHeader()}
        onClose={handleClose}
        showCloseIcon={showCloseIcon}
        hideCancelButton={!isTaskModal}
        backdrop="static"
        extraModalProps={{
          centered: true,
          enforceFocus: false,
          'aria-labelledby': 'contained-modal-title-vcenter',
        }}
        onSubmit={onSubmit}
        submitButtonText={
          isTaskModal ? (
            getIsRequestPending(createTaskRequestStatus) ? (
              <>
                <Spinner animation="border" size="sm" />
                <span className="ml-2">Adding Task...</span>
              </>
            ) : (
              'Add Task'
            )
          ) : (
            'Save'
          )
        }
        submitButtonClassName="header-btn"
        cancelButtonClassName="cancel-task-btn"
        isSubmitLoading={
          getIsRequestPending(addSequenceStepRequestStatus) ||
          getIsRequestPending(addSequenceStepVariantRequestStatus) ||
          getIsRequestPending(updateSequenceStepVariantDataRequestStatus)
        }
        isSubmitDisabled={
          getIsRequestPending(addSequenceStepRequestStatus) ||
          getIsRequestPending(addSequenceStepVariantRequestStatus) ||
          getIsRequestPending(updateSequenceStepVariantDataRequestStatus) ||
          !isFormValid(errors) ||
          (stepType === StepType.Task && plainTextInMailValue?.length === 0) ||
          isSubmitDisabled(selectedStepType, {
            content: stepContentHTML,
            subject: plainTextInMailValue,
          }) ||
          isWhatsappCharacterLimitExceeded ||
          hasErrorChips ||
          getIsRequestPending(createTaskRequestStatus)
        }
        footerContent={
          isTaskModal ? (
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Checkboxes
                  checked={isTaskCompleted}
                  onChange={() => setIsTaskCompleted(!isTaskCompleted)}
                  className="mark-as-completed-checkbox-new"
                />
                <span className="mark-as-completed-checkbox-label">
                  Mark as completed
                </span>
              </div>
            </div>
          ) : (
            <div className="day-input-spinner d-flex align-items-center">
              <label
                className="regular-2 font-medium popover-arrow-color-txt d-flex align-items-center m-0"
                htmlFor="days"
              >
                {label}
                <InputNumber
                  id="days"
                  name="relativeDays"
                  className="day-number m-0 ml-2 mr-2"
                  min={SequenceStepExecutionDay.MinDay}
                  max={SequenceStepExecutionDay.MaxDay}
                  value={absoluteDays}
                  onChange={onInputChangeHelper}
                  onKeyDown={onKeyDownHandler}
                />
              </label>
              <span className="regular-1 font-10 gray-txt-15">
                <span className="regular-1 font-10 gray-txt-15">
                  {getStepScheduledString(
                    absoluteDays,
                    mode !== SequenceStepAction.CreateStep
                      ? actionStep?.number
                      : isSubsequence
                      ? subsequenceStepNumber
                      : stepNumber,
                    getDaysAndStepsObj(
                      isSubsequence ? subsequenceSteps : steps,
                    ),
                  )}
                </span>
              </span>
            </div>
          )
        }
      >
        {/* Select Task Sub Type */}
        {isSubTypeSectionVisible(stepType) && (
          <StepTaskType
            label={getSubTypeSectionLabel(stepType)}
            stepType={stepType}
            selectedTaskType={selectedStepSubType}
            onChange={(type) => handleStepType(type)}
            mode={mode}
          />
        )}

        <div className="w-100 mt-3 pr-4 pl-4 content-body">
          {/* InMail Subject */}
          {(selectedStepType === SequenceStepType.LinkedInInMail ||
            selectedStepType === SequenceStepType.Custom) && (
            <div className="w-100 mb-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex">
                  <span className="semibold-1 gray-txt-15">
                    {selectedStepType === SequenceStepType.Custom
                      ? 'Task Title'
                      : 'InMail Subject'}
                  </span>
                  <OverlayTooltip
                    text={
                      selectedStepType === SequenceStepType.Custom
                        ? 'Task title is mandatory'
                        : 'Write personalized InMail subject line'
                    }
                    className="tooltip-mdd"
                  >
                    <InfoCircle
                      width={18}
                      height={18}
                      className="gray-txt-11 ml-2"
                    />
                  </OverlayTooltip>
                </div>
                {selectedStepType === SequenceStepType.LinkedInInMail &&
                  renderMergeTagSelection(false)}
              </div>
              <SingleEditorInput
                key={selectedStepType}
                inputValue={inMailValue}
                inputRef={inMailRef}
                onInputChange={onInMailSubjectChange}
                placeholder={
                  selectedStepType === SequenceStepType.LinkedInInMail
                    ? 'Add a subject line here'
                    : 'Add a task title'
                }
                onInputBlur={onInputBlur}
                inputClassName="create-step-input-box mt-2"
                enableMergeTag={false}
                enableSpintax={false}
                enableEmoji={false}
                showTitle=""
                mergeTagOptions={mergeTagOptions}
                showInlineCount={true}
                maxInputCount={
                  selectedStepType === SequenceStepType.LinkedInInMail
                    ? 200
                    : 100
                }
                getEditorInstance={(editor) => {
                  inMailEditorRef.current = editor;
                }}
                error={errors?.inMail}
              />

              {errors.inMail && (
                <span className="regular-1 red-txt-12 line-height-18 mt-1">
                  {getErrorString(
                    errors.inMail,
                    convertHtmlToText(stripChipTagsFromHtml(inMailValue)),
                    subjectInputMaxLength(stepType),
                    selectedStepType === SequenceStepType.LinkedInInMail
                      ? 2
                      : 5,
                  )}
                </span>
              )}
            </div>
          )}

          {/* Add Task Message / Description */}
          <div className="d-flex align-items-center justify-content-between w-100">
            <div>
              <span className="semibold-1 gray-txt-15">
                {getStepContentSectionLabel(selectedStepType)}
              </span>
              <OverlayTooltip
                text={getSequenceTypeTooltipText(selectedStepType)}
                className="tooltip-mdd"
              >
                <InfoCircle
                  width={18}
                  height={18}
                  className="gray-txt-11 ml-2"
                />
              </OverlayTooltip>
            </div>
            {!shouldUseEditor && renderMergeTagSelection()}
          </div>

          {/* Editor with expand functionality for InMail */}
          <div className="position-relative create-step-modal-textarea">
            {/* Expand button for InMail step types */}
            {getIsExpandOptionVisible(stepType, selectedStepSubType) && (
              <div className="d-flex justify-content-end position-absolute expand message-expand-modal">
                <span className="regular-1 gray-txt-11">
                  <ArrowsDiagonal
                    width={16}
                    height={16}
                    className="gray-txt-15 pointer"
                    onClick={showModal}
                  />
                </span>
              </div>
            )}

            <Editor
              key={`editor-${
                shouldUseEditor ? 'whatsapp' : 'general'
              }-${selectedStepSubType}-${stepType}`}
              value={
                shouldUseEditor
                  ? stepContentHTML
                  : getIsTaskNoteContent(selectedStepType)
                  ? taskNote?.trim()
                  : stepContentHTML?.trim()
              }
              onEditorChange={onContentChange}
              error={errors.content || hasErrorChips}
              className={getEditorClassName()}
              showMinimalToolbar={shouldUseEditor}
              showAddAttachments={shouldUseEditor}
              showMergeAndVariableTags={true}
              mergeTags={mergeTagOptions}
              addMergeTagInContent={handleAddMergeTagInContent}
              addVariableTagInContent={handleAddVariableTagInContent}
              getEditorInstance={(editor) => {
                editorRef.current = editor;
              }}
              maxInputCount={
                shouldUseEditor
                  ? WHATSAPP_MAX_LIMIT
                  : contentMaxLength[selectedStepSubType] ||
                    contentMaxLength[stepType]
              }
              showCount
              placeholder={
                shouldUseEditor
                  ? 'Enter the message you want to send to prospects.'
                  : getPlaceHolderText(selectedStepSubType, stepType)
              }
              isTemplateEditorModal={false}
              hideHeader={!shouldUseEditor}
              disableSpinTagConversion={true}
              {...(shouldUseEditor && {
                attachments,
                onAttachmentStatusChange: handleAttachmentStatusChange,
                onAttachmentsChange: handleAttachmentsChange,
                onAttachmentsMetaChange: handleAttachmentsMetaChange,
                sendAttachmentUploadRequest: sendUploadAttachmentRequest,
                uploadAttachmentRequestStatus,
                uploadAttachmentRequestError,
                uploadedAttachment,
                resetUploadAttachmentRequest,
                sendDeleteAttachmentRequest,
                deleteAttachmentRequestStatus,
                deleteAttachmentRequestError,
                deletedAttachmentId,
                resetDeleteAttachmentRequest,
                imageUploader: handleImageUploader,
                uniqueId,
                attachmentsClassname: 'whatsapp-attachments',
                sequenceStepVariantId: actionVariant?.id,
              })}
            />

            {errors?.content && !hasErrorChips && (
              <div className="editor-error-message-container">
                <ImageIcon src={Images.AlertTriangleRed} alt="Alert" />
                <div className="editor-error-message">
                  Character limit exceeds
                </div>
              </div>
            )}
            {/* Handle error chip */}
            {hasErrorChips && (
              <div className="editor-error-message-container">
                <ImageIcon src={Images.AlertTriangleRed} alt="Alert" />
                <div className="editor-error-message">
                  Error chip not allowed
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="pr-4 pl-4">
          {/* Add Task Note */}
          {selectedStepType !== SequenceStepType.LinkedInPostInteraction &&
            selectedStepType !== SequenceStepType.LinkedInViewProfile &&
            selectedStepType !== SequenceStepType.Custom &&
            !isCallTask(selectedStepType) && (
              <TaskNote
                taskNote={taskNote}
                mergeTagOptions={mergeTagOptions}
                onTaskNoteSave={(note: string) => setTaskNote(note)}
                onContentBlur={() => onContentBlur(true)}
              />
            )}

          {/* Custom Button */}
          {/* {selectedStepType === SequenceStepType.Custom && (
          <CustomButton
            buttonName={buttonName}
            buttonLink={buttonLink}
            errors={errors}
            onSwitchChange={(e) => onSwitchChange(e)}
            onButtonNameChange={(e) => onButtonNameChange(e)}
            // onButtonLinkChange={(e) => console.log('link', e)}
            onButtonLinkChange={(e) => setButtonLink(e)}
            onButtonLinkBlur={onCustomButtonConfigBlur}
          />
        )} */}

          {isTaskModal && (
            <div className="d-flex justify-content-between flex-grow-1 gap-12 mt-3 prospect-task-assignee">
              <div className="d-flex flex-column gap-12 task-due-date">
                <span className="semibold-1 gray-txt-15">Due Date</span>
                <DatePicker
                  selected={dueDate}
                  onChange={(date) => handleDueDateChange(date)}
                  minDate={new Date()}
                  dateFormat="MMM dd, yyyy"
                  customInput={
                    <DatePickerInput
                      onClear={() => handleDueDateChange(null)}
                    />
                  }
                  wrapperClassName="due-date-picker-wrapper"
                />
              </div>
              <TaskAssignee
                taskAssigneeList={taskAssigneeList}
                selectedAssignee={taskAssignee}
                onAssigneeChange={(option) => setTaskAssignee(option?.value)}
                disabled={isAgencyClientUser()}
              />
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <TaskPriority
              selectedPriority={selectedPriority}
              onPriorityChange={(priority) => setSelectedPriority(priority)}
            />
            {!isTaskModal && (
              <TaskAssignee
                taskAssigneeList={taskAssigneeList}
                selectedAssignee={selectedAssignee}
                onAssigneeChange={(option) =>
                  setSelectedAssignee(option?.value)
                }
                disabled={isAgencyClientUser()}
              />
            )}
          </div>
        </div>
      </Modal>
      {/* Confirmation Modal  */}
      {isConfirmationModalVisible && (
        <SendTestEmailModal
          show={isConfirmationModalVisible}
          title={
            <div className="send-test-email-modal-header">
              <img src={Images.InfoCircleYellow} alt="Icon" />

              <h2>Want to close the editor without saving changes?</h2>
            </div>
          }
          className="save-changes-modal"
          content={getConfirmationModalContent()}
          onSubmit={onClose}
          onClose={() => setIsConfirmationModalVisible(false)}
          showInput={false}
          submitButtonText="Close"
          isSubmitLoading={false}
          isSubmitDisabled={false}
          cancelButtonText="Cancel"
        />
      )}
      {/* Content Area Modal */}
      {isAddTaskModalVisible && (
        <TaskNoteModal
          show={isAddTaskModalVisible}
          title={getTaskNoteModalTitle(selectedStepType)}
          maxLength={getTaskNoteModalMaxLength(selectedStepType)}
          onClose={hideModal}
          isExpandOptionVisible={true}
          mergeTagOptions={mergeTagOptions}
          contentAreaPlaceholder={getPlaceHolderText(
            selectedStepSubType,
            stepType,
          )}
          taskNote={
            getIsTaskNoteContent(selectedStepType) ? taskNote : stepContentHTML
          }
          onSubmit={(value: string) => onContentChange(value)}
          useEditor={true}
        />
      )}
    </>
  );
};

export default CreateStepModal;
